import { ReactNode, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';

interface ProtectedRouteProps {
  children: ReactNode;
  requireVerified?: boolean;
  requireModerator?: boolean;
}

export default function ProtectedRoute({ 
  children, 
  requireVerified = false,
  requireModerator = false 
}: ProtectedRouteProps) {
  const { user, profile, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!loading) {
      // Not logged in - redirect to login
      if (!user) {
        navigate('/login', { 
          state: { from: location.pathname },
          replace: true 
        });
        return;
      }

      // Require verified member
      if (requireVerified && profile?.account_type === 'pending') {
        navigate('/', { replace: true });
        return;
      }

      // Require moderator
      if (requireModerator && profile?.account_type !== 'moderator') {
        navigate('/', { replace: true });
        return;
      }

      // Check if suspended or banned
      if (profile?.account_type === 'suspended' || profile?.account_type === 'banned') {
        navigate('/', { replace: true });
        return;
      }
    }
  }, [user, profile, loading, navigate, location, requireVerified, requireModerator]);

  // Show loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-teal-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Show nothing while redirecting
  if (!user || 
      (requireVerified && profile?.account_type === 'pending') ||
      (requireModerator && profile?.account_type !== 'moderator') ||
      profile?.account_type === 'suspended' || 
      profile?.account_type === 'banned') {
    return null;
  }

  return <>{children}</>;
}
